/** QuickHouse — Rendu 2D pur du plan (grille, pièces, murs, ouvertures, cotes, aperçus) */
import { wallAngle, wallLength, pointOnWall, fmtLen } from '../../shared/geometry.js';
import { wallSolidSpans, effectiveOpeningsForWall } from '../../shared/plan3d.js';
import { roomRings, roomArea, bulgeHandles, roomHandles, roomEdgeLabels } from '../../shared/rooms.js';
import { stairPlan } from '../../shared/stairs.js';
import { ITEM_CATEGORIES, findCatalogItem } from '../../shared/catalog.js';

/** Teinte stable par pièce (hachage de l'id) */
export function roomHue(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
}

const COLORS = {
  bg: '#0c0e14',
  gridFine: '#10141f',
  grid: '#161b28',
  gridMajor: '#1d2435',
  wall: '#d8dee9',
  wallSelected: '#3b9eff',
  watermark: 'rgba(122, 132, 156, 0.28)',
  opening: '#9fb4d8',
  openingSelected: '#3b9eff',
  dim: '#7a849c',
  room: '#e5a73b',
  preview: 'rgba(59, 158, 255, 0.8)',
  snap: '#3fbf7f',
};

/** Dessine tout le plan. ui = { preview, snapPoint, selection, hover }, catalog = catalogue d'objets */
export function draw(ctx, { width, height, dpr, floor, lowerFloor, view, ui, catalog = [], gridSize = 10, north = 0 }) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.setTransform(dpr * view.zoom, 0, 0, dpr * view.zoom, dpr * view.panX, dpr * view.panY);

  drawGrid(ctx, width, height, view, gridSize);
  if (lowerFloor) {
    drawFloorWalls(ctx, lowerFloor, view, { watermark: true });
    // Accès venant de l'étage inférieur : arrivée en pointillés (trémie)
    drawStairs(ctx, lowerFloor, view, ui.selection, { ghost: true });
  }
  if (floor) {
    drawRoomFills(ctx, floor, view, ui.selection);
    drawStairs(ctx, floor, view, ui.selection);
    drawFloorWalls(ctx, floor, view, { selection: ui.selection, hover: ui.hover });
    drawOpenings(ctx, floor, view, ui.selection);
    drawRooms(ctx, floor, view, ui.selection);
    drawItems(ctx, floor, view, ui.selection, catalog);
    drawBeams(ctx, floor, view, ui.selection);
    drawDims(ctx, floor, view);
    drawManualDims(ctx, floor, view, ui.selection);
  }
  if (ui.guides?.length) drawGuides(ctx, ui.guides, width, height, view);
  if (ui.preview) drawPreview(ctx, ui.preview, view);
  if (ui.rubber) drawRubber(ctx, ui.rubber, view);
  if (ui.snapPoint) drawSnapPoint(ctx, ui.snapPoint, view);
  if (floor && ui.selection?.length === 1 && ui.selection[0].kind === 'wall') {
    drawWallHandles(ctx, floor, ui.selection[0], view);
  }

  // Rose du nord (coin haut droit, espace écran)
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawNorthRose(ctx, width, north);
}

/** Boussole : flèche du nord selon le réglage du projet (0° = nord en haut du plan) */
function drawNorthRose(ctx, width, north) {
  const cx = width - 34;
  const cy = 34;
  const a = (north * Math.PI) / 180;
  const nx = Math.sin(a);
  const ny = -Math.cos(a);
  ctx.save();
  ctx.strokeStyle = '#2a3450';
  ctx.fillStyle = 'rgba(12, 14, 20, 0.7)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, 20, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // Flèche : pointe rouge côté nord, queue grise
  ctx.strokeStyle = '#7a849c';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - nx * 13, cy - ny * 13);
  ctx.lineTo(cx, cy);
  ctx.stroke();
  ctx.strokeStyle = '#e06c60';
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + nx * 13, cy + ny * 13);
  ctx.stroke();
  ctx.fillStyle = '#e06c60';
  ctx.font = '600 10px "JetBrains Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('N', cx + nx * 27, cy + ny * 27);
  ctx.restore();
}

/** La sélection est une liste : vrai si (kind, id) en fait partie */
function isSel(selection, kind, id) {
  return Array.isArray(selection) && selection.some((s) => s.kind === kind && s.id === id);
}

// ── Grille ────────────────────────────────────────────────────
function drawGrid(ctx, width, height, view, gridSize = 10) {
  // Trois niveaux : grille réglée (jusqu'au mm), repère 10 cm, repère 1 m.
  // Un niveau n'est dessiné que si ses carreaux font au moins ~4 px à l'écran.
  const tiers = [...new Set([gridSize, 10, 100])].sort((a, b) => a - b)
    .filter((s) => s * view.zoom >= 4);
  if (!tiers.length) tiers.push(100);
  const colors = [COLORS.gridFine, COLORS.grid, COLORS.gridMajor].slice(3 - tiers.length);
  tiers.forEach((s, i) => drawGridLines(ctx, width, height, view, s, colors[i]));
}

function drawGridLines(ctx, width, height, view, step, color) {
  const x0 = -view.panX / view.zoom;
  const y0 = -view.panY / view.zoom;
  const x1 = x0 + width / view.zoom;
  const y1 = y0 + height / view.zoom;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1 / view.zoom;
  ctx.beginPath();
  for (let x = Math.floor(x0 / step) * step; x <= x1; x += step) {
    ctx.moveTo(x, y0); ctx.lineTo(x, y1);
  }
  for (let y = Math.floor(y0 / step) * step; y <= y1; y += step) {
    ctx.moveTo(x0, y); ctx.lineTo(x1, y);
  }
  ctx.stroke();
}

// ── Murs ──────────────────────────────────────────────────────
function drawFloorWalls(ctx, floor, view, { watermark = false, selection = null, hover = null } = {}) {
  const visibleOpenings = floor.openings.filter((o) => !o.hidden);
  for (const wall of floor.walls) {
    if (wall.hidden) continue;
    // Ouvertures effectives : y compris celles des murs colinéaires qui débordent ici
    const spans = watermark
      ? [{ u0: 0, u1: wallLength(wall) }]
      : wallSolidSpans(wall, effectiveOpeningsForWall(wall, floor.walls, visibleOpenings));
    const selected = isSel(selection, 'wall', wall.id);
    const hovered = hover?.kind === 'wall' && hover.id === wall.id;
    ctx.strokeStyle = watermark ? COLORS.watermark : (selected ? COLORS.wallSelected : (hovered ? '#eef2f9' : COLORS.wall));
    ctx.lineWidth = wall.thickness;
    ctx.lineCap = 'butt';
    const angle = wallAngle(wall);
    ctx.beginPath();
    for (const span of spans) {
      const p0 = pointOnWall(wall, span.u0);
      const p1 = pointOnWall(wall, span.u1);
      ctx.moveTo(p0.x, p0.y);
      // segment nul (mur totalement ouvert) : éviter un trait invisible
      if (span.u1 - span.u0 < 0.5) ctx.lineTo(p0.x + Math.cos(angle) * 0.5, p0.y + Math.sin(angle) * 0.5);
      else ctx.lineTo(p1.x, p1.y);
    }
    ctx.stroke();
  }
}

// ── Ouvertures : symboles par type/variante ───────────────────
function drawOpenings(ctx, floor, view, selection) {
  const wallsById = new Map(floor.walls.map((w) => [w.id, w]));
  for (const op of floor.openings) {
    if (op.hidden) continue;
    const selected = isSel(selection, 'opening', op.id);
    const color = selected ? COLORS.openingSelected : COLORS.opening;
    // Velux : pas de mur, rectangle pointillé sur le plan (toit)
    if (!op.wallId) {
      if (op.position) drawVelux(ctx, op, view, color);
      continue;
    }
    const wall = wallsById.get(op.wallId);
    if (!wall || wall.hidden) continue;
    const center = pointOnWall(wall, op.offset);
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(wallAngle(wall));
    ctx.scale(op.meta?.flipH ? -1 : 1, op.meta?.flipV ? -1 : 1);
    ctx.strokeStyle = color;
    drawOpeningSymbol(ctx, op, wall.thickness, view, color);
    ctx.restore();
  }
}

/** Symbole en repère local : origine = centre de l'ouverture, x le long du mur */
function drawOpeningSymbol(ctx, op, t, view, color) {
  const w = op.width;
  const variant = op.variant ?? 'single';
  const thin = 1 / view.zoom;
  const thick = Math.max(2 / view.zoom, 1.5);

  const leaf = (hingeX, dir, len) => { // battant + arc (dir = +1 vers +x)
    ctx.lineWidth = thick;
    ctx.beginPath();
    ctx.moveTo(hingeX, -t / 2);
    ctx.lineTo(hingeX, -t / 2 - len);
    ctx.stroke();
    ctx.lineWidth = thin;
    ctx.beginPath();
    if (dir > 0) ctx.arc(hingeX, -t / 2, len, -Math.PI / 2, 0);
    else ctx.arc(hingeX, -t / 2, len, Math.PI, Math.PI * 1.5);
    ctx.stroke();
  };
  const frame = () => { // cadre vitré : rectangle + double trait
    ctx.lineWidth = 1.5 / view.zoom;
    ctx.strokeRect(-w / 2, -t / 2, w, t);
    ctx.beginPath();
    ctx.moveTo(-w / 2, -t / 6); ctx.lineTo(w / 2, -t / 6);
    ctx.moveTo(-w / 2, t / 6); ctx.lineTo(w / 2, t / 6);
    ctx.stroke();
  };
  const sliding = (pocket = false) => { // deux vantaux décalés + flèches
    ctx.lineWidth = thick;
    ctx.beginPath();
    ctx.moveTo(-w / 2, -t / 6); ctx.lineTo(w * 0.08, -t / 6);
    ctx.moveTo(-w * 0.08, t / 6); ctx.lineTo(w / 2, t / 6);
    ctx.stroke();
    ctx.lineWidth = thin;
    arrow(ctx, { x: -w * 0.3, y: -t / 2 - 12 }, { x: w * 0.05, y: -t / 2 - 12 }, view);
    if (pocket) { // galandage : refoulement dans le mur, en pointillés
      ctx.setLineDash([8, 5]);
      ctx.strokeRect(w / 2, -t / 3, w / 2, (2 * t) / 3);
      ctx.setLineDash([]);
    }
  };

  if (op.type === 'passage') {
    // Ouverture libre : traits pointillés joignant les tableaux
    ctx.lineWidth = thin;
    ctx.setLineDash([8, 5]);
    ctx.beginPath();
    ctx.moveTo(-w / 2, -t / 2); ctx.lineTo(w / 2, -t / 2);
    ctx.moveTo(-w / 2, t / 2); ctx.lineTo(w / 2, t / 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Hauteur basse non nulle : double trait (allège conservée)
    if (op.sillHeight > 0) {
      ctx.beginPath();
      ctx.moveTo(-w / 2, 0); ctx.lineTo(w / 2, 0);
      ctx.stroke();
    }
  } else if (op.type === 'door') {
    if (variant === 'double' || variant === 'doubleGlazed') {
      leaf(-w / 2, +1, w / 2);
      leaf(w / 2, -1, w / 2);
      if (variant === 'doubleGlazed') {
        // Vitrage : double trait dans l'épaisseur, comme une fenêtre
        ctx.lineWidth = thin;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.moveTo(-w / 2, -t / 6); ctx.lineTo(w / 2, -t / 6);
        ctx.moveTo(-w / 2, t / 6); ctx.lineTo(w / 2, t / 6);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    } else if (variant === 'sliding') {
      sliding();
    } else {
      leaf(-w / 2, +1, w);
    }
  } else if (op.type === 'garage') {
    if (variant === 'double') {
      leaf(-w / 2, +1, w / 2);
      leaf(w / 2, -1, w / 2);
    } else if (variant === 'sliding') {
      sliding();
    } else { // volet roulant : coffre intérieur + trait
      ctx.lineWidth = 1.5 / view.zoom;
      ctx.beginPath();
      ctx.moveTo(-w / 2, 0); ctx.lineTo(w / 2, 0);
      ctx.stroke();
      ctx.strokeRect(-w / 2, t / 2, w, 14); // coffre côté intérieur
      ctx.lineWidth = thin;
      for (let x = -w / 2 + 10; x < w / 2; x += 25) { // lames du volet
        ctx.beginPath();
        ctx.moveTo(x, t / 2); ctx.lineTo(x, t / 2 + 14);
        ctx.stroke();
      }
    }
  } else { // window
    if (variant === 'bay') {
      sliding();
      frame();
    } else if (variant === 'pocket') {
      sliding(true);
    } else if (variant === 'french') {
      frame();
      leaf(-w / 2, +1, w * 0.8);
    } else if (variant === 'frenchDouble') {
      frame();
      leaf(-w / 2, +1, w / 2);
      leaf(w / 2, -1, w / 2);
    } else if (variant === 'double') {
      frame();
      ctx.lineWidth = thin;
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.arc(-w / 2, -t / 2, w / 2, -Math.PI / 2, 0);
      ctx.arc(w / 2, -t / 2, w / 2, Math.PI, Math.PI * 1.5);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.moveTo(0, -t / 2); ctx.lineTo(0, t / 2);
      ctx.stroke();
    } else { // single
      frame();
      ctx.lineWidth = thin;
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.arc(-w / 2, -t / 2, w, -Math.PI / 2, -Math.PI / 6);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }
}

function drawVelux(ctx, op, view, color) {
  const { x, y } = op.position;
  const w = op.width;
  const h = op.height; // profondeur sur le plan
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 / view.zoom;
  ctx.setLineDash([10, 6]);
  ctx.strokeRect(x - w / 2, y - h / 2, w, h);
  ctx.beginPath();
  ctx.moveTo(x - w / 2, y - h / 2); ctx.lineTo(x + w / 2, y + h / 2);
  ctx.moveTo(x + w / 2, y - h / 2); ctx.lineTo(x - w / 2, y + h / 2);
  ctx.stroke();
  ctx.setLineDash([]);
}

function arrow(ctx, a, b, view) {
  ctx.beginPath();
  ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const s = 8;
  ctx.moveTo(b.x, b.y);
  ctx.lineTo(b.x - s * Math.cos(ang - 0.4), b.y - s * Math.sin(ang - 0.4));
  ctx.moveTo(b.x, b.y);
  ctx.lineTo(b.x - s * Math.cos(ang + 0.4), b.y - s * Math.sin(ang + 0.4));
  ctx.stroke();
}

// ── Escaliers : plein en partie basse, pointillés en partie haute ──
// ghost = escalier venant de l'étage inférieur (arrivée) : tout en pointillés grisés.
function drawStairs(ctx, floor, view, selection, { ghost = false } = {}) {
  for (const stair of floor.stairs ?? []) {
    if (stair.hidden) continue;
    const selected = !ghost && isSel(selection, 'stair', stair.id);
    const color = ghost ? 'rgba(122, 132, 156, 0.55)' : (selected ? COLORS.wallSelected : '#a9b6d0');
    const plan = stairPlan(stair);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 / view.zoom;
    for (const seg of [...plan.edges, ...plan.steps]) {
      ctx.setLineDash(ghost || seg.t > 0.5 ? [10, 6] : []);
      ctx.beginPath();
      ctx.moveTo(seg.a.x, seg.a.y);
      ctx.lineTo(seg.b.x, seg.b.y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    if (plan.arrow && !ghost) {
      ctx.lineWidth = 1.2 / view.zoom;
      arrow(ctx, plan.arrow.a, plan.arrow.b, view);
    }
  }
}

// ── Pièces : sols colorés ─────────────────────────────────────
function ringPath(ctx, ring) {
  ctx.moveTo(ring[0].x, ring[0].y);
  for (let i = 1; i < ring.length; i++) ctx.lineTo(ring[i].x, ring[i].y);
  ctx.closePath();
}

function drawRoomFills(ctx, floor, view, selection) {
  for (const room of floor.rooms) {
    if (!room.blocks?.length || room.hidden) continue;
    const selected = isSel(selection, 'room', room.id);
    const isSlab = room.kind === 'slab';
    ctx.beginPath();
    for (const { outer, holes } of roomRings(room)) {
      ringPath(ctx, outer);
      for (const h of holes) ringPath(ctx, h);
    }
    if (room.kind === 'roof') {
      // Toit : couleur du matériau, contour plein, hachures de pente
      const roofColors = { slate: '71, 80, 94', tile: '179, 89, 58', flat: '154, 160, 168', green: '93, 139, 74' };
      const rgb = roofColors[room.material] ?? roofColors.flat;
      ctx.fillStyle = `rgba(${rgb}, ${selected ? 0.35 : 0.20})`;
      ctx.fill('evenodd');
      ctx.strokeStyle = selected ? COLORS.wallSelected : `rgba(${rgb}, 0.9)`;
      ctx.lineWidth = (selected ? 2 : 1.4) / view.zoom;
      ctx.stroke();
      continue;
    }
    if (isSlab) {
      // Dalle : gris neutre ; mezzanine (élévation > 0) : contour en pointillés
      ctx.fillStyle = `rgba(138, 143, 153, ${selected ? 0.30 : 0.16})`;
      ctx.fill('evenodd');
      ctx.strokeStyle = selected ? COLORS.wallSelected : 'rgba(160, 168, 182, 0.8)';
      ctx.lineWidth = (selected ? 2 : 1.4) / view.zoom;
      if ((room.elevation ?? 0) > 0) ctx.setLineDash([12, 7]);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      const hue = roomHue(room.id);
      ctx.fillStyle = `hsla(${hue}, 45%, 45%, ${selected ? 0.38 : 0.22})`;
      ctx.fill('evenodd');
      ctx.strokeStyle = selected ? COLORS.wallSelected : `hsla(${hue}, 45%, 62%, 0.75)`;
      ctx.lineWidth = (selected ? 2 : 1.2) / view.zoom;
      ctx.stroke();
    }
  }
}

// ── Étiquettes de pièces (+ poignées de courbure si sélection unique) ──
function drawRooms(ctx, floor, view, selection) {
  for (const room of floor.rooms) {
    if (room.hidden) continue;
    const selected = isSel(selection, 'room', room.id);
    ctx.save();
    ctx.translate(room.position.x, room.position.y);
    ctx.scale(1 / view.zoom, 1 / view.zoom);
    ctx.font = '600 14px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = selected ? COLORS.wallSelected : COLORS.room;
    ctx.fillText(room.name, 0, 0);
    if (room.blocks?.length) {
      const m2 = roomArea(room) / 10000;
      ctx.font = '11px "JetBrains Mono", monospace';
      ctx.fillStyle = COLORS.dim;
      ctx.fillText(`${m2.toFixed(1)} m²`, 0, 16);
    }
    ctx.restore();
    // Sélection unique : poignées d'édition + cotes des côtés
    if (selected && selection.length === 1 && room.blocks?.length) {
      const r = 4.5 / view.zoom;
      // Coins de rect / sommets de poly (carrés bleus)
      ctx.fillStyle = COLORS.wallSelected;
      for (const h of roomHandles(room)) {
        ctx.fillRect(h.p.x - r, h.p.y - r, r * 2, r * 2);
      }
      // Courbure des côtés de poly (cercles verts)
      ctx.fillStyle = COLORS.snap;
      for (const h of bulgeHandles(room)) {
        ctx.beginPath();
        ctx.arc(h.p.x, h.p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      // Cotes des côtés
      for (const e of roomEdgeLabels(room)) {
        ctx.save();
        ctx.translate(e.p.x, e.p.y);
        ctx.rotate(Math.abs(e.angle) > Math.PI / 2 ? e.angle + Math.PI : e.angle);
        ctx.scale(1 / view.zoom, 1 / view.zoom);
        ctx.font = '10px "JetBrains Mono", monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = COLORS.wallSelected;
        ctx.fillText(fmtLen(e.len), 0, -4);
        ctx.restore();
      }
    }
  }
}

// ── Guides d'alignement (références d'accrochage) ─────────────
function drawGuides(ctx, guides, width, height, view) {
  const x0 = -view.panX / view.zoom;
  const y0 = -view.panY / view.zoom;
  const x1 = x0 + width / view.zoom;
  const y1 = y0 + height / view.zoom;
  ctx.strokeStyle = 'rgba(63, 191, 127, 0.65)';
  ctx.lineWidth = 1 / view.zoom;
  ctx.setLineDash([7 / view.zoom, 5 / view.zoom]);
  ctx.beginPath();
  for (const g of guides) {
    if (g.type === 'v') { ctx.moveTo(g.x, y0); ctx.lineTo(g.x, y1); }
    else { ctx.moveTo(x0, g.y); ctx.lineTo(x1, g.y); }
  }
  ctx.stroke();
  ctx.setLineDash([]);
  // Croix sur le point de référence
  for (const g of guides) {
    if (!g.ref) continue;
    const s = 5 / view.zoom;
    ctx.beginPath();
    ctx.moveTo(g.ref.x - s, g.ref.y);
    ctx.lineTo(g.ref.x + s, g.ref.y);
    ctx.moveTo(g.ref.x, g.ref.y - s);
    ctx.lineTo(g.ref.x, g.ref.y + s);
    ctx.stroke();
  }
}

// ── Objets de la bibliothèque ─────────────────────────────────
/** Empreinte rectangulaire tournée + code du symbole, couleur par catégorie */
export function drawItemSymbol(ctx, view, entry, position, rotation, scale = 1, style = 'normal') {
  const w = entry.size.w * scale;
  const d = entry.size.d * scale;
  const color = ITEM_CATEGORIES[entry.category]?.color ?? '#9aa0a8';
  ctx.save();
  ctx.translate(position.x, position.y);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.fillStyle = style === 'selected' ? 'rgba(59, 158, 255, 0.30)'
    : style === 'preview' ? 'rgba(59, 158, 255, 0.18)' : `${color}26`;
  ctx.strokeStyle = style === 'selected' ? '#3b9eff' : color;
  ctx.lineWidth = (style === 'selected' ? 2 : 1.3) / view.zoom;
  if (style === 'preview') ctx.setLineDash([8, 5]);
  ctx.fillRect(-w / 2, -d / 2, w, d);
  ctx.strokeRect(-w / 2, -d / 2, w, d);
  ctx.setLineDash([]);
  // Trait d'orientation (face avant = −y local)
  ctx.beginPath();
  ctx.moveTo(-w / 2, -d / 2);
  ctx.lineTo(w / 2, -d / 2);
  ctx.lineWidth = 3 / view.zoom;
  ctx.stroke();
  // Code du symbole (taille écran constante)
  ctx.rotate(-(rotation * Math.PI) / 180);
  ctx.scale(1 / view.zoom, 1 / view.zoom);
  ctx.font = '600 10px "JetBrains Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = style === 'selected' ? '#3b9eff' : color;
  ctx.fillText(entry.code, 0, 0);
  ctx.restore();
}

function drawItems(ctx, floor, view, selection, catalog) {
  for (const it of floor.items ?? []) {
    if (it.hidden) continue;
    const entry = findCatalogItem(catalog, it.catalogId);
    if (!entry) continue;
    const selected = isSel(selection, 'item', it.id);
    drawItemSymbol(ctx, view, entry, it.position, it.rotation ?? 0, it.scale ?? 1, selected ? 'selected' : 'normal');
    if (it.label && view.zoom > 0.25) {
      ctx.save();
      ctx.translate(it.position.x, it.position.y);
      ctx.scale(1 / view.zoom, 1 / view.zoom);
      ctx.font = '9px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = COLORS.dim;
      ctx.fillText(it.label, 0, 14);
      ctx.restore();
    }
  }
}

// ── Cotes manuelles (outil Mesure) ────────────────────────────
export function drawDimLine(ctx, view, a, b, { color = '#e5a73b', dashed = false } = {}) {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len < 1) return;
  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const tick = 8 / view.zoom;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2 / view.zoom;
  if (dashed) ctx.setLineDash([8, 5]);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.setLineDash([]);
  // Traits d'attache obliques aux extrémités (convention cote)
  for (const p of [a, b]) {
    ctx.beginPath();
    ctx.moveTo(p.x - tick * Math.cos(ang + Math.PI / 4), p.y - tick * Math.sin(ang + Math.PI / 4));
    ctx.lineTo(p.x + tick * Math.cos(ang + Math.PI / 4), p.y + tick * Math.sin(ang + Math.PI / 4));
    ctx.stroke();
  }
  // Longueur au milieu, au-dessus de la ligne
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  ctx.save();
  ctx.translate(mid.x, mid.y);
  ctx.rotate(Math.abs(ang) > Math.PI / 2 ? ang + Math.PI : ang);
  ctx.scale(1 / view.zoom, 1 / view.zoom);
  ctx.font = '600 11px "JetBrains Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = color;
  ctx.fillText(`${fmtLen(len)} cm`, 0, -4);
  ctx.restore();
}

function drawManualDims(ctx, floor, view, selection) {
  for (const dim of floor.dims ?? []) {
    if (dim.hidden) continue;
    const selected = isSel(selection, 'dim', dim.id);
    drawDimLine(ctx, view, dim.a, dim.b, { color: selected ? COLORS.wallSelected : '#e5a73b' });
  }
}

// ── Charpente : poutres et poteaux ────────────────────────────
const BEAM_2D_COLORS = { wood: '#b0824f', 'wood-dark': '#8a6238', steel: '#8a93a3', white: '#d8d8d2' };

function drawBeamShape(ctx, beam, view, { selected = false, preview = false } = {}) {
  const color = preview ? COLORS.preview : (selected ? COLORS.wallSelected : (BEAM_2D_COLORS[beam.material] ?? BEAM_2D_COLORS.wood));
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.4 / view.zoom;
  if (beam.kind === 'post') {
    const d = beam.d ?? beam.w;
    ctx.strokeRect(beam.p.x - beam.w / 2, beam.p.y - d / 2, beam.w, d);
    // Croix du poteau (symbole charpente)
    ctx.beginPath();
    ctx.moveTo(beam.p.x - beam.w / 2, beam.p.y - d / 2);
    ctx.lineTo(beam.p.x + beam.w / 2, beam.p.y + d / 2);
    ctx.moveTo(beam.p.x + beam.w / 2, beam.p.y - d / 2);
    ctx.lineTo(beam.p.x - beam.w / 2, beam.p.y + d / 2);
    ctx.stroke();
    return;
  }
  // Poutre : bande de largeur w le long de l'axe + axe pointillé
  const ang = Math.atan2(beam.b.y - beam.a.y, beam.b.x - beam.a.x);
  const nx = Math.sin(ang) * beam.w / 2;
  const ny = -Math.cos(ang) * beam.w / 2;
  ctx.beginPath();
  ctx.moveTo(beam.a.x + nx, beam.a.y + ny);
  ctx.lineTo(beam.b.x + nx, beam.b.y + ny);
  ctx.moveTo(beam.a.x - nx, beam.a.y - ny);
  ctx.lineTo(beam.b.x - nx, beam.b.y - ny);
  ctx.stroke();
  ctx.setLineDash([8 / view.zoom, 6 / view.zoom]);
  ctx.lineWidth = 1 / view.zoom;
  ctx.beginPath();
  ctx.moveTo(beam.a.x, beam.a.y);
  ctx.lineTo(beam.b.x, beam.b.y);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawBeams(ctx, floor, view, selection) {
  for (const beam of floor.beams ?? []) {
    if (beam.hidden) continue;
    drawBeamShape(ctx, beam, view, { selected: isSel(selection, 'beam', beam.id) });
  }
}

// ── Cotes ─────────────────────────────────────────────────────
function drawDims(ctx, floor, view) {
  if (view.zoom < 0.12) return;
  for (const wall of floor.walls) {
    if (wall.hidden) continue;
    const len = wallLength(wall);
    if (len < 20) continue;
    const angle = wallAngle(wall);
    const mid = pointOnWall(wall, len / 2);
    const off = wall.thickness / 2 + 14 / view.zoom;
    const nx = Math.sin(angle);
    const ny = -Math.cos(angle);
    ctx.save();
    ctx.translate(mid.x + nx * off, mid.y + ny * off);
    ctx.rotate(Math.abs(angle) > Math.PI / 2 ? angle + Math.PI : angle);
    ctx.scale(1 / view.zoom, 1 / view.zoom);
    ctx.font = '11px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = COLORS.dim;
    ctx.fillText(fmtLen(len), 0, 0);
    ctx.restore();
  }
}

// ── Aperçus / snap ────────────────────────────────────────────
function drawPreview(ctx, preview, view) {
  if (preview.type === 'wall') {
    ctx.strokeStyle = COLORS.preview;
    ctx.lineWidth = preview.thickness ?? 20;
    ctx.lineCap = 'butt';
    ctx.setLineDash([10, 6]);
    ctx.beginPath();
    ctx.moveTo(preview.a.x, preview.a.y);
    ctx.lineTo(preview.b.x, preview.b.y);
    ctx.stroke();
    ctx.setLineDash([]);
    // Longueur en cours
    const len = Math.hypot(preview.b.x - preview.a.x, preview.b.y - preview.a.y);
    if (len > 5) {
      const mid = { x: (preview.a.x + preview.b.x) / 2, y: (preview.a.y + preview.b.y) / 2 };
      ctx.save();
      ctx.translate(mid.x, mid.y);
      ctx.scale(1 / view.zoom, 1 / view.zoom);
      ctx.font = '600 12px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = COLORS.preview;
      ctx.fillText(`${fmtLen(len)} cm`, 0, -14);
      ctx.restore();
    }
  } else if (preview.type === 'roomRect') {
    const x = Math.min(preview.a.x, preview.b.x);
    const y = Math.min(preview.a.y, preview.b.y);
    const w = Math.abs(preview.b.x - preview.a.x);
    const h = Math.abs(preview.b.y - preview.a.y);
    ctx.fillStyle = 'rgba(229, 167, 59, 0.15)';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = COLORS.room;
    ctx.lineWidth = 1.5 / view.zoom;
    ctx.setLineDash([8, 5]);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
    if (w > 5 && h > 5) {
      ctx.save();
      ctx.translate(x + w / 2, y + h / 2);
      ctx.scale(1 / view.zoom, 1 / view.zoom);
      ctx.font = '600 12px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = COLORS.room;
      ctx.fillText(`${fmtLen(w)} × ${fmtLen(h)} cm`, 0, 0);
      ctx.restore();
    }
  } else if (preview.type === 'roomPoly') {
    const pts = [...preview.points, preview.cursor];
    ctx.strokeStyle = COLORS.room;
    ctx.lineWidth = 1.5 / view.zoom;
    ctx.setLineDash([8, 5]);
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.stroke();
    ctx.setLineDash([]);
    // Marqueur du premier point (fermeture)
    ctx.fillStyle = COLORS.snap;
    ctx.beginPath();
    ctx.arc(pts[0].x, pts[0].y, 5 / view.zoom, 0, Math.PI * 2);
    ctx.fill();
  } else if (preview.type === 'stair') {
    const plan = stairPlan(preview.stair);
    ctx.strokeStyle = COLORS.preview;
    ctx.lineWidth = 1.5 / view.zoom;
    ctx.setLineDash([8, 5]);
    for (const seg of [...plan.edges, ...plan.steps]) {
      ctx.beginPath();
      ctx.moveTo(seg.a.x, seg.a.y);
      ctx.lineTo(seg.b.x, seg.b.y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  } else if (preview.type === 'velux') {
    drawVelux(ctx, { position: preview.position, width: preview.width, height: preview.height }, view, COLORS.preview);
  } else if (preview.type === 'measure') {
    drawDimLine(ctx, view, preview.a, preview.b, { color: COLORS.preview, dashed: true });
  } else if (preview.type === 'beam') {
    drawBeamShape(ctx, { kind: 'beam', a: preview.a, b: preview.b, w: preview.w ?? 15 }, view, { preview: true });
  } else if (preview.type === 'post') {
    drawBeamShape(ctx, { kind: 'post', p: preview.p, w: preview.w ?? 15, d: preview.w ?? 15 }, view, { preview: true });
  } else if (preview.type === 'item') {
    drawItemSymbol(ctx, view, preview.entry, preview.position, preview.rotation ?? 0, 1, 'preview');
  } else if (preview.type === 'opening') {
    const { wall, u, width, kind } = preview;
    const center = pointOnWall(wall, u);
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(wallAngle(wall));
    ctx.fillStyle = kind === 'door' ? 'rgba(59, 158, 255, 0.45)' : 'rgba(63, 191, 127, 0.45)';
    ctx.fillRect(-width / 2, -wall.thickness / 2 - 2, width, wall.thickness + 4);
    ctx.restore();
  }
}

/** Rectangle de sélection en cours */
function drawRubber(ctx, rubber, view) {
  const x = Math.min(rubber.a.x, rubber.b.x);
  const y = Math.min(rubber.a.y, rubber.b.y);
  const w = Math.abs(rubber.b.x - rubber.a.x);
  const h = Math.abs(rubber.b.y - rubber.a.y);
  ctx.fillStyle = 'rgba(59, 158, 255, 0.10)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = COLORS.preview;
  ctx.lineWidth = 1 / view.zoom;
  ctx.setLineDash([6 / view.zoom, 4 / view.zoom]);
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
}

function drawSnapPoint(ctx, p, view) {
  ctx.strokeStyle = COLORS.snap;
  ctx.lineWidth = 1.5 / view.zoom;
  const r = 6 / view.zoom;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.stroke();
}

function drawWallHandles(ctx, floor, selection, view) {
  const wall = floor.walls.find((w) => w.id === selection.id);
  if (!wall) return;
  const r = 5 / view.zoom;
  ctx.fillStyle = COLORS.wallSelected;
  for (const p of [wall.a, wall.b]) {
    ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
  }
}
